import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { MatInput, MatLabel } from '@angular/material/input';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormField } from '@angular/material/form-field';
import { MatButton, MatIconButton } from '@angular/material/button';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
    AppConfigService,
    AuthStorageService,
    AuthenticationService,
    DocumentTitleService,
    AuthProviderEnum,
    AuthUrlHelperService,
    AuthorizationService,
    AuthProvider
} from '@sphere/platform';
import * as z from 'zod';
import { catchError, map, tap } from 'rxjs/operators';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatButtonLoading } from '@ng-matero/extensions/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { firstValueFrom, Observable, of } from 'rxjs';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { AlertComponent } from '@sphere/components';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { HolidaySnowEffectComponent } from './components/holiday-snow-effect.component';
import { HalloweenEffectComponent } from './components/halloween-effect.component';

const UserPublicParams = z.object({
    OidcAuthority: z.string(),
    SsoProvider: z.string(),
    IdpHint: z.optional(z.string())
});

type UserPublicParams = z.infer<typeof UserPublicParams>;

const defaultUserPublicParams: UserPublicParams = {
    SsoProvider: 'redhat',
    OidcAuthority: '',
    IdpHint: ''
};

@Component({
    templateUrl: './login-page.component.html',
    styleUrl: './login-page.component.scss',
    imports: [
        MatFormField,
        MatLabel,
        MatInput,
        FormsModule,
        MatButton,
        ReactiveFormsModule,
        MatButtonLoading,
        MatProgressSpinner,
        RouterLink,
        AlertComponent,
        MatIconButton,
        MatIcon,
        MatMenuTrigger,
        MatMenu,
        MatMenuItem,
        HolidaySnowEffectComponent,
        HalloweenEffectComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginPageComponent implements OnInit {
    private readonly config = inject(AppConfigService);
    private readonly title = inject(DocumentTitleService);
    private readonly authStorage = inject(AuthStorageService);
    private readonly authUrlHelper = inject(AuthUrlHelperService);
    private readonly authentication = inject(AuthenticationService);
    private readonly authorization = inject(AuthorizationService);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly http = inject(HttpClient);

    public readonly usernameValue = signal('');
    public readonly validUsernameValue = computed(() => {
        return this.usernameValue()?.length > 0;
    });

    public readonly loadingAuthenticationProvider = signal(false);
    public readonly loading = signal(true);
    public readonly loadingLoginCheck = signal(true);

    public readonly loggedIn = signal(false);
    public readonly loggedInUser = signal<string>('');

    public readonly hasLastUserCookie = signal(false);
    public readonly lastUser = signal<string>('');
    public readonly lastSsoProvider = signal<AuthProviderEnum | ''>('');

    public readonly error = signal('');

    public readonly currentYear = computed(() => {
        return new Date().getFullYear();
    });

    public readonly loadingPrincipal = this.authorization.loadingPrincipal;
    public readonly loadedPrincipal = this.authorization.loadedPrincipal;

    constructor() {
        const navigation = this.router.currentNavigation();
        const logoutReason = navigation.finalUrl.queryParamMap.get('reason');
        const relog = navigation.finalUrl.queryParamMap.get('relog');
        if (navigation && navigation.extras?.state?.error) {
            this.error.set(navigation.extras?.state?.error);
        } else if (logoutReason) {
            this.error.set(logoutReason);
        }
        if (relog) {
            this.authStorage.deleteAuthProperties();
        }
        explicitEffect(
            [this.authentication.initialized, this.authentication.isAuthenticated],
            async ([isAuthenticationInitialized, isAuthenticated]) => {
                if (relog) {
                    return;
                }
                if (isAuthenticationInitialized && !isAuthenticated) {
                    await this.attemptLogin();
                } else if (isAuthenticationInitialized && isAuthenticated) {
                    // Check if the provider has a redirect URL (e.g., Auth0's appState)
                    const providerRedirectUrl = await this.authentication.getRedirectUrl();
                    const returnUrl = providerRedirectUrl || this.route.snapshot.queryParamMap.get('returnUrl') || '/';
                    await this.router.navigateByUrl(returnUrl, { replaceUrl: true });
                }
            },
            {
                defer: true
            }
        );
    }

    async ngOnInit() {
        this.title.setTitle('Log-in');

        const url = this.router.routerState.snapshot.url;
        const parsedUrl = this.router.parseUrl(url);

        const { authProvider, idpHint, loginHint } = await this.authUrlHelper.getQuerySsoParams(parsedUrl);
        const init = await this.authentication.initialize(authProvider, idpHint, loginHint);
        const storedLoginHint = await this.authStorage.getLoginHint();
        if (init && this.authentication.isAuthenticated()) {
            this.loggedIn.set(true);
            this.loggedInUser.set(this.authentication.currentUser.username || this.authentication.currentUser.email);
            // Check if the provider has a redirect URL (e.g., Auth0's appState)
            const providerRedirectUrl = await this.authentication.getRedirectUrl();
            const returnUrl = providerRedirectUrl || this.route.snapshot.queryParamMap.get('returnUrl') || '/';
            await this.router.navigateByUrl(returnUrl, { replaceUrl: true });
            this.loading.set(false);
            return;
        } else if (init && storedLoginHint?.value) {
            this.hasLastUserCookie.set(true);
            this.lastUser.set(storedLoginHint?.value);
            this.lastSsoProvider.set(this.authentication.authProvider as AuthProviderEnum);
            this.loading.set(false);
            return;
        }
        this.loading.set(false);
    }

    public fetchAuthenticationProvider(): Observable<UserPublicParams> {
        this.loadingAuthenticationProvider.set(true);
        return this.http
            .post<UserPublicParams>(
                `${this.config.ssoParamsUrl}/ssoapi/v1/sso_parameters`,
                JSON.stringify({
                    userName: this.usernameValue(),
                    userAgent: 'Sphere Web Client'
                }),
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            )
            .pipe(
                map(res => {
                    const result = UserPublicParams.safeParse(res);
                    if (!result.success) {
                        console.warn(result.error);
                        return defaultUserPublicParams;
                    } else {
                        return result.data;
                    }
                }),
                catchError((error: HttpErrorResponse) => {
                    this.loadingAuthenticationProvider.set(false);
                    this.loading.set(false);
                    if (error.status === 404) {
                        this.error.set('Invalid username. Please try again.');
                    } else {
                        this.error.set(error.message);
                        return of(defaultUserPublicParams);
                    }
                    return of(null);
                }),
                tap(() => {
                    this.loadingAuthenticationProvider.set(false);
                })
            );
    }

    public async submitUsername() {
        this.loading.set(true);
        if (this.usernameValue()?.length > 0) {
            const userPublicParams = await firstValueFrom(this.fetchAuthenticationProvider());
            if (!userPublicParams) {
                this.loading.set(false);
                return null;
            }
            await this.authStorage.setLoginHint(this.usernameValue());
            const ssoProvider = userPublicParams.SsoProvider?.toLowerCase();
            if (ssoProvider && typeof ssoProvider === 'string' && this.authStorage.isSupportedAuthProvider(ssoProvider)) {
                await this.authStorage.setAuthProvider(ssoProvider);
            }
            if (userPublicParams?.IdpHint && typeof userPublicParams.IdpHint === 'string') {
                await this.authStorage.setIdpHint(userPublicParams.IdpHint);
            }
            if (userPublicParams?.OidcAuthority && typeof userPublicParams.OidcAuthority === 'string') {
                await this.authStorage.setOidcAuthority(userPublicParams.OidcAuthority);
            }
            if (this.authentication.initialized()) {
                await this.authentication.reInitializeForProvider(userPublicParams.SsoProvider);
            }
            const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || '/';
            await this.router.navigateByUrl(returnUrl);
        }
    }

    private async attemptLogin() {
        this.loadingLoginCheck.set(true);
        const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || '/home';
        await this.authentication.login({ redirectTo: returnUrl });
        this.loadingLoginCheck.set(true);
        await this.router.navigateByUrl(returnUrl);
    }

    public async logout() {
        await this.authentication?.logout();
        this.router.navigate(['/']);
    }

    public async continueAs(username: string, authProvider: AuthProviderEnum | '') {
        if (authProvider && this.authStorage.isSupportedAuthProvider(authProvider)) {
            await this.authStorage.setAuthProvider(authProvider);
            await this.authStorage.setLoginHint(username);
            await this.attemptLogin();
        }
    }

    public async continueAsDifferentUser() {
        await this.authStorage.deleteAuthProperties();
        this.hasLastUserCookie.set(false);
        this.lastUser.set('');
        this.lastSsoProvider.set('');
    }

    public async useRedhatSSOLogin(loginHint?: string) {
        await this.forceAuthProvider(AuthProviderEnum.keycloak, loginHint);
    }
    public async useAuth0Login(loginHint?: string) {
        await this.forceAuthProvider(AuthProviderEnum.auth0, loginHint);
    }

    private async forceAuthProvider(authProvider: AuthProvider, loginHint?: string) {
        if (loginHint && loginHint !== 'legacy_user') {
            await this.authStorage.setLoginHint(loginHint);
        }
        await this.authStorage.setAuthProvider(authProvider);
        if (this.authentication.initialized()) {
            await this.authentication.reInitializeForProvider(authProvider);
        }
        const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || '/';
        await this.router.navigateByUrl(returnUrl, { state: { relogOnLogin: true } });
    }
}
